/**
 * The town beyond the rails (lifted from lantern-water's `backdrop-town.js`,
 * with its `finish`): the foundations of the upper quarters, dense row houses
 * behind the terraces with alleys, a taller second row, the north-east corner
 * with its balcony, awning and warm panes, and washing across the alleys. A
 * world-coordinate set piece: placed at the origin. Its own seed (52361);
 * nothing here is walkable or collides. No params.
 */
import * as THREE from 'three';
import { atlasMesh, backdropMaterial, type V3 } from '../backdrop-art.ts';
import { BAY, type RoofKind, type Style, TINTS, createBackdropBuilder } from '../backdrop-kit.ts';
import { createBackdropProps } from '../backdrop-props.ts';
import type { Prefab } from './types.ts';

type Row = readonly [x: number, front: number, bays: number, deep: number, floors: number, style: Style, tint: V3, roof: RoofKind, roofMat: 'roof' | 'slate'];

export const prefab: Prefab = {
  id: 'backdrop_town',
  buildPrefab(ctx) {
    const p = new THREE.Group();
    p.name = 'backdrop-town';
    const { m, box } = ctx;
    const material = backdropMaterial(ctx.kit, m.windowCool);
    const b = createBackdropBuilder(ctx, 52361);
    const props = createBackdropProps(ctx, b);
    const { house } = b;
    const roofOf = (rm: 'roof' | 'slate'): THREE.Material => (rm === 'slate' ? m.roofDark : m.roof);

    // Foundations (the same footprints as the prototype's createTown).
    box(p, -8, 1.0, -24, 24, 2, 13, m.stone);
    box(p, 16, 1.48, -24, 11, 2.96, 13, m.stone);
    box(p, -8, 2.02, -24, 24, 0.08, 13, m.paving);
    box(p, 16, 3.0, -24, 11, 0.08, 13, m.paving);
    box(p, -28.6, -0.15, -15, 13.2, 1.5, 30, m.stone);
    box(p, -28.6, 0.62, -15, 13.2, 0.06, 30, m.paving);
    box(p, -40.2, -0.4, -23, 10, 1.2, 22, m.stone);
    box(p, -40.2, 0.22, -23, 10, 0.04, 22, m.paving);

    // North row behind the upper terraces, facing the town.
    const NW = 2.05;
    const NE = 3.03;
    const north: readonly Row[] = [
      [-17.55, -21.0, 3, 3, 3, 'plaster', TINTS.ochre, 'gableX', 'roof'],
      [-12.9, -21.5, 3, 3, 4, 'timber', TINTS.straw, 'gableZ', 'slate'],
      [-9.75, -21.2, 2, 3, 3, 'brick', TINTS.warm, 'flat', 'slate'],
      [-5.0, -20.8, 4, 3, 3, 'plaster', TINTS.teal, 'gableX', 'slate'],
      [-0.62, -21.3, 3, 3, 4, 'plaster', TINTS.amber, 'hip', 'roof'],
      [2.75, -21.0, 1.6, 3, 4, 'timber', TINTS.sage, 'gableZ', 'roof'],
    ];
    const northEast: readonly Row[] = [
      [12.4, -21.2, 3, 3, 3, 'timber', TINTS.rust, 'gableZ', 'roof'],
      [16.4, -20.8, 3, 3, 3, 'plaster', TINTS.clay, 'gableX', 'slate'],
      [20.0, -21.4, 2, 3, 4, 'brick', TINTS.warm, 'gableZ', 'roof'],
    ];
    const place = (list: readonly Row[], base: number): void => {
      for (const [x, front, bays, deep, floors, style, tint, roof, rm] of list) {
        house(p, { x, z: front - (deep * BAY) / 2, bays, deep, floors, base, style, tint, roof, roofMat: roofOf(rm), face: 'south', side: 'east', passage: bays >= 3 && floors >= 3 });
      }
    };
    place(north, NW);
    place(northEast, NE);

    // North-east corner beyond the garden terraces: big value shapes that survive the far blur.
    box(p, 27.8, 0.6, -29.8, 12.6, 2.0, 7.6, m.stone);
    box(p, 27.8, 1.62, -29.8, 12.6, 0.06, 7.6, m.paving);
    const NC = 1.65;
    const corner: readonly (readonly [number, number, number, number, Style, V3, RoofKind, 'roof' | 'slate', boolean])[] = [
      [22.9, 2, 2, -26.4, 'plaster', TINTS.amber, 'gableZ', 'roof', false],
      [26.975, 3, 4, -27.3, 'timber', TINTS.straw, 'gableX', 'slate', true],
      [30.9, 2, 3, -26.6, 'plaster', TINTS.rust, 'flat', 'roof', false],
    ];
    for (const [x, bays, floors, front, style, tint, roof, rm, dormers] of corner) {
      house(p, { x, z: front - 1.875, bays, deep: 3, floors, base: NC, style, tint, roof, roofMat: roofOf(rm), face: 'south', side: 'west', lit: 0.35, ...(dormers ? { dormers: true } : {}) });
    }
    props.alley(24.15, 25.1, -30.0, -26.9, NC, 6.5);
    props.alley(28.85, 29.65, -30.0, -26.9, NC, 6.5);
    // A timber balcony across the tall house, and a striped shop awning below.
    box(p, 26.975, NC + 2 * 1.6 + 0.05, -27.3 + 0.3, 3.9, 0.12, 0.6, m.darkWood);
    for (let k = 0; k < 9; k += 1) box(p, 25.1 + 0.1 + k * 0.46, NC + 2 * 1.6 + 0.4, -27.3 + 0.56, 0.05, 0.6, 0.05, m.darkWood);
    box(p, 26.975, NC + 2 * 1.6 + 0.72, -27.3 + 0.58, 3.9, 0.07, 0.07, m.darkWood);
    const awning = box(p, 30.9, NC + 1.45, -26.6 + 0.42, 2.7, 0.07, 0.95, m.red);
    awning.rotation.x = 0.42;
    // A few panes in the town's own warm window glass, and cream cornices along each eave.
    for (const [x0, front, bay, row] of [
      [21.65, -26.4, 0, 1],
      [25.1, -27.3, 1, 1],
      [25.1, -27.3, 2, 3],
      [25.1, -27.3, 0, 2],
      [29.65, -26.6, 1, 2],
      [21.65, -26.4, 1, 0],
      [25.1, -27.3, 2, 2],
      [29.65, -26.6, 0, 1],
      [29.65, -26.6, 1, 0],
    ] as const)
      box(p, x0 + (bay + 0.5) * 1.25, NC + row * 1.6 + 0.94, front + 0.025, 0.5, 0.78, 0.04, m.windowGlass);
    for (const [x, w, top, front] of [
      [22.9, 2.5, NC + 2 * 1.6, -26.4],
      [26.975, 3.75, NC + 4 * 1.6, -27.3],
      [30.9, 2.5, NC + 3 * 1.6, -26.6],
    ] as const)
      box(p, x, top - 0.16, front + 0.08, w + 0.2, 0.14, 0.18, m.cream);
    // A taller rank behind breaks the skyline over the first row.
    for (const [x, bays, floors, tint, roof, rm] of [
      [23.4, 3, 5, TINTS.teal, 'gableZ', 'roof'],
      [30.2, 3, 5, TINTS.ochre, 'hip', 'slate'],
    ] as const)
      house(p, { x, z: -31.6, bays, deep: 2, floors, base: NC, style: 'plaster', tint, roof, roofMat: roofOf(rm), face: 'south', side: 'west', cheap: true, dormers: false, door: false, lit: 0.3 });
    // The taller second row: only roofs and upper storeys clear the first.
    for (const [x, front, bays, floors, tint, roof, rm, base] of [
      [-16.5, -26.2, 4, 4, TINTS.straw, 'gableX', 'slate', NW],
      [-10.8, -26.8, 3, 5, TINTS.rust, 'gableZ', 'roof', NW],
      [-4.2, -26.0, 4, 4, TINTS.sage, 'hip', 'slate', NW],
      [1.4, -26.5, 3, 5, TINTS.ochre, 'gableX', 'roof', NW],
      [14.2, -26.4, 4, 4, TINTS.teal, 'gableX', 'roof', NE],
      [19.4, -26.0, 3, 5, TINTS.straw, 'hip', 'slate', NE],
    ] as const)
      house(p, { x, z: front - 1.875, bays, deep: 3, floors, base, style: 'plaster', tint, roof, roofMat: roofOf(rm), face: 'south', side: 'east', cheap: true, dormers: false, door: false });
    // Shadowed alley slots between the first-row houses.
    for (const [x0, x1, base] of [
      [-15.6, -14.8, NW],
      [-8.5, -7.5, NW],
      [1.4, 2.0, NW],
      [14.1, 14.9, NE],
    ] as const)
      props.alley(x0, x1, -24.5, -21.0, base, 5.5);
    // Washing strung across the alleys between upper windows.
    for (const [x0, x1, base, z] of [
      [-15.7, -14.7, NW, -21.6],
      [-8.6, -7.4, NW, -22.2],
      [14.0, 15.0, NE, -21.8],
    ] as const)
      props.laundry(p, x0, z, x1, z, base + 2.6, 2, false);

    atlasMesh(p, b.kit, material, 'backdrop-atlas');
    const root = new THREE.Group();
    root.add(p);
    return { root };
  },
};
